import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert-dialog';
import {
  ColumnConfig,
  DataTable,
  DataTablePagination,
  defineTableSchema,
  useConfiguredTable,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { useFilters } from '@art-of-hospitality-solutions-llc/ui-kit/components/filters';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import type { UserRoleAssignment } from '@Types/userRole';
import { formatNumberWithCommas } from '@Utils/utils';
import { UserIcon } from 'lucide-react';
import { useState } from 'react';
import service from '@Service';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import AsyncSelect from 'react-select/async';
import { useMessages } from '@Contexts/messages';

// ---------- column configs ----------

const userRoleUserColumnConfig = (
  handleRemoveUserFromRole: (row: UserRoleAssignment) => void,
): ColumnConfig<UserRoleAssignment>[] => [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.user?.name,
    type: 'text',
    size: 120,
  },
  {
    id: 'email',
    headerLabel: 'Email',
    accessor: (row) => row.user?.email ?? '',
    type: 'text',
  },
  {
    id: 'id',
    headerLabel: '',
    accessor: (row) => row,
    type: 'text',
    size: 111,
    cell: (info) => {
      return (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline">Remove</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove User from Role?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove the Role from the user.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleRemoveUserFromRole(info.row?.original)}
              >
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    },
    enableSorting: false,
    hideFromFilters: true,
    meta: {
      dataNoClick: true,
    },
  },
];

const fallbackData = [];

type Option = {
  value: string | number;
  label: string;
};

const UserRoleUserList = ({ fetchUserRole, users, roleId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnPinning, setColumnPinning] = useState({
    left: ['select'],
    right: ['id'],
  });
  const [selectedContactOption, setSelectedContactOption] = useState(null);
  const { searchAllUsers } = useMessages();

  const { filters, addOrUpdateFilter, removeFilter } = useFilters([]);

  const handleRemoveUserFromRole = async (info: UserRoleAssignment) => {
    const res = await service.removeUserFromRole(info.userRoleId, info.userId);
    if (res.id) {
      fetchUserRole();
      setDialogOpen(false);
    }
  };

  const { columns } = defineTableSchema(
    userRoleUserColumnConfig(handleRemoveUserFromRole),
  );

  const sortBy = 'name';

  const { table } = useConfiguredTable({
    data: users ?? fallbackData,
    columns,
    filters,
    initialSorting: [{ id: sortBy, desc: true }],
    pagination: {
      pageIndex,
      pageSize,
      setPageIndex,
      setPageSize,
    },
    columnPinning,
    setColumnPinning,
    autoResetPageOnFilterChange: true,
  });

  const customStyles = {
    control: (styles) => ({
      ...styles,
      borderRadius: '8px',
    }),
  };

  const loadOptions = async (search: string): Promise<Option[]> => {
    const data = await searchAllUsers(search);
    const options = Array.isArray(data) ? data : (data.data ?? []);

    return options.map((user) => {
      return {
        value: user.id,
        label:
          `${user.name || 'N/A'} (${user.email || user.phoneNumber || 'No Contact Info'})`.trim(),
      };
    });
  };

  const handleAddUserToRole = async () => {
    const res = await service.addUserToRole(
      roleId,
      selectedContactOption.value,
    );
    if (res.id) {
      fetchUserRole();
      setDialogOpen(false);
    }
  };

  return (
    <div className="mt-2 space-y-4">
      <div className="flex h-16 w-full items-center justify-between gap-4 rounded-lg bg-zinc-100 p-4">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-200">
            <UserIcon className="h-4 w-4 text-zinc-500" />
          </div>
          <div>
            {'Current Users: '}
            {!users ? (
              <Skeleton className="inline h-6 w-8" />
            ) : (
              <span className="font-bold">
                {users ? formatNumberWithCommas(users.length) : 0}
              </span>
            )}
          </div>
        </div>
        <div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>Add User to Role</Button>
            </DialogTrigger>
            <DialogContent overrideSrollBehavior={true}>
              <DialogHeader>
                <DialogTitle>Add User to Role</DialogTitle>
              </DialogHeader>
              <AsyncSelect
                // isMulti
                loadOptions={loadOptions}
                value={selectedContactOption}
                onChange={(selected) => {
                  setSelectedContactOption(selected || null);
                }}
                placeholder="Search users..."
                classNamePrefix="select"
                styles={customStyles}
                noOptionsMessage={() => 'No user found'}
              />
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Close</Button>
                </DialogClose>
                <Button onClick={handleAddUserToRole}>Confirm</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Input
          placeholder="Filter by name..."
          value={
            filters.find((f) => f.field === 'name')?.value.toString() ?? ''
          }
          onChange={(e) => {
            const value = e.target.value;
            if (value) {
              addOrUpdateFilter({
                field: 'name',
                operator: 'contains',
                value,
                displayName: 'Name',
                displayValue: value,
              });
            } else {
              removeFilter('name');
            }
          }}
          className="w-64"
        />

        <Input
          placeholder="Filter by email..."
          value={
            filters.find((f) => f.field === 'email')?.value.toString() ?? ''
          }
          onChange={(e) => {
            const value = e.target.value;
            if (value) {
              addOrUpdateFilter({
                field: 'email',
                operator: 'contains',
                value,
                displayName: 'Email',
                displayValue: value,
              });
            } else {
              removeFilter('email');
            }
          }}
          className="w-64"
        />
      </div>

      <DataTable
        table={table}
        onRowClick={(row) => {
          console.log('navigate to the user details page', row.original);
        }}
        isLoading={!users}
        skeletonRowCount={pageSize}
      />
      <DataTablePagination
        table={table}
        meta={{ total: table.getFilteredRowModel()?.rows?.length }}
        pagination={{ size: pageSize, number: pageIndex + 1 }}
        setPage={({ size, number }) => {
          setPageSize(size);
          setPageIndex(number - 1);
        }}
        setPageNumber={(n) => setPageIndex(n - 1)}
        isLoading={!users}
      />
    </div>
  );
};

export default UserRoleUserList;
